import { useMemo } from 'react';

import {
  shouldNotRenderAboutSection,
  shouldRenderLocationSection
} from '../utils';
import { MarketableLocation } from 'Src/types';

import messages from '../messages';

export const LOCATION_SECTION_IDS = {
  ABOUT: 'about-section',
  LOCATION: 'location-section',
  INSURANCES: 'insurance-section',
  SERVICES: 'services-section',
  SPECIALTIES: 'specialties-section',
  PROVIDERS: 'providers-section'
};

interface UseLocationSectionProps {
  location: MarketableLocation | null | undefined;
  totalProviders: number | undefined;
}

export interface NavItem {
  id: string;
  name: string;
  message: {
    id: string;
    description: string;
    defaultMessage: string;
  };
  rendered: boolean;
  href: string;
}

interface UseLocationSectionReturnType {
  navItems: NavItem[];
  rendered: {
    aboutSection: boolean;
    locationSection: boolean;
    insuranceSection: boolean;
    servicesSection: boolean;
    specialtiesSection: boolean;
    providersSection: boolean;
  };
}
/**
 * hook that manages:
 * - the rendered state of each section
 * - the nav items for each section
 */
export const useLocationSections = ({
  location,
  totalProviders
}: UseLocationSectionProps): UseLocationSectionReturnType => {
  const rendered = useMemo(() => {
    const aboutSection = location
      ? !shouldNotRenderAboutSection(location)
      : false;
    const locationSection = location
      ? shouldRenderLocationSection(location)
      : false;
    const insuranceSection = Boolean(location?.insurance_accepted?.length);
    const servicesSection = Boolean(location?.services?.length);
    const specialtiesSection = Boolean(location?.specialties?.length);
    const providersSection = Boolean(totalProviders);

    return {
      aboutSection,
      locationSection,
      insuranceSection,
      servicesSection,
      specialtiesSection,
      providersSection
    };
  }, [location, totalProviders]);

  const navItems = useMemo(() => {
    const getNavItem = (sectionId: string): NavItem | null => {
      switch (sectionId) {
        case LOCATION_SECTION_IDS.ABOUT:
          return {
            id: LOCATION_SECTION_IDS.ABOUT,
            href: `#${LOCATION_SECTION_IDS.ABOUT}`,
            name: 'About',
            message: messages.aboutNavTitle,
            rendered: rendered.aboutSection
          };
        case LOCATION_SECTION_IDS.LOCATION:
          return {
            id: LOCATION_SECTION_IDS.LOCATION,
            href: `#${LOCATION_SECTION_IDS.LOCATION}`,
            name: 'Location',
            message: messages.locationNavTitle,
            rendered: rendered.locationSection
          };
        case LOCATION_SECTION_IDS.INSURANCES:
          return {
            id: LOCATION_SECTION_IDS.INSURANCES,
            href: `#${LOCATION_SECTION_IDS.INSURANCES}`,
            name: 'Insurance',
            message: messages.insuranceNavTitle,
            rendered: rendered.insuranceSection
          };
        case LOCATION_SECTION_IDS.SERVICES:
          return {
            id: LOCATION_SECTION_IDS.SERVICES,
            href: `#${LOCATION_SECTION_IDS.SERVICES}`,
            name: 'Services',
            message: messages.servicesNavTitle,
            rendered: rendered.servicesSection
          };
        case LOCATION_SECTION_IDS.SPECIALTIES:
          return {
            id: LOCATION_SECTION_IDS.SPECIALTIES,
            href: `#${LOCATION_SECTION_IDS.SPECIALTIES}`,
            name: 'Specialties',
            message: messages.specialtiesNavTitle,
            rendered: rendered.specialtiesSection
          };
        case LOCATION_SECTION_IDS.PROVIDERS:
          return {
            id: LOCATION_SECTION_IDS.PROVIDERS,
            href: `#${LOCATION_SECTION_IDS.PROVIDERS}`,
            name: 'Providers',
            message: messages.providersNavTitle,
            rendered: rendered.providersSection
          };
      }
      return null;
    };

    return Object.values(LOCATION_SECTION_IDS)
      .map((sectionId) => getNavItem(sectionId))
      .filter(Boolean) as NavItem[];
  }, [rendered]);

  return {
    navItems,
    rendered
  };
};
